import DateFnsUtils from "@date-io/date-fns";
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from "@material-ui/pickers";
import plLocale from "date-fns/locale/pl";
import { compose } from "ramda";
import { memo } from "react";
import { Control, Controller } from "react-hook-form";

import { generateFormattedDate } from "../../utilities/functions";
import { minDate, maxDate } from "../../utilities/validators";
import { withError } from "../HOC";
import { Tooltip } from "../Tooltip";
import { CommonFormElProps } from "../types";

const utils = new DateFnsUtils();

export type DatePickerProps = {
  readonly control: Control;
  readonly isSubmitted: boolean;
  readonly yearOnly?: boolean;
  readonly tooltip?: string;
} & CommonFormElProps;

export const DatePicker = memo<DatePickerProps>(
  ({
    control,
    defaultValue,
    hasError,
    id,
    isSubmitted,
    required = false,
    label: labelTxt,
    yearOnly = false,
    tooltip,
  }) => {
    const componentView = yearOnly ? "year" : "date";
    const dateFormat = yearOnly ? "yyyy" : "dd/MM/yyyy";
    const label = (
      <>
        {labelTxt}
        <Tooltip copy={tooltip} />
      </>
    );

    return (
      <Controller
        name={id}
        control={control}
        defaultValue={defaultValue}
        render={({ onChange, onBlur, value, ref }) => (
          <MuiPickersUtilsProvider utils={DateFnsUtils} locale={plLocale}>
            <KeyboardDatePicker
              autoOk
              error={hasError}
              format={dateFormat}
              id={id}
              inputRef={ref}
              inputVariant="outlined"
              helperText=""
              InputProps={{ notched: false }}
              InputLabelProps={{
                required,
                shrink: true,
              }}
              KeyboardButtonProps={{
                "aria-label": "zmień datę",
              }}
              label={label}
              minDate={minDate}
              maxDate={maxDate}
              name={id}
              onChange={(e) => {
                let date: string | typeof e = e;

                if (utils.isValid(e)) {
                  date = generateFormattedDate(
                    new Date(e?.toDateString() as string)
                  );

                  if ((e as Date).getTime() > new Date().getTime()) {
                    date = "invalid date";
                  }
                }

                onChange(date);

                if (isSubmitted) {
                  onBlur();
                }
              }}
              onClose={() => onBlur()}
              placeholder={dateFormat.toLowerCase()}
              variant="inline"
              value={value || null}
              views={[componentView]}
            />
          </MuiPickersUtilsProvider>
        )}
      />
    );
  }
);

export const DatePickerWithError = compose(withError)(DatePicker);
